import { useCallback, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { userSelectors } from 'store/features/user/selectors'
import { aiAvatarSelectors } from 'store/features/aiAvatar/selectors'
import { editorSelectors } from 'store/features/editor/selectors'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { creditsSelectors } from 'store/features/credits/selectors'
import { MediaStatus } from 'types/media'
import { useOpenUpgradePopup } from 'hooks/useOpenUpgradePopup'
import { REF_LINKS } from 'consts/refLinks'
import { aiAvatarThunks } from 'store/features/aiAvatar/thunks'
import { editorThunks } from 'store/features/editor/thunks'
import { checkTtsLanguageMatch_API } from 'api/tts'
import { clampSpeechTextForUser } from 'store/features/aiAvatar/utils'
import { dispatch } from 'store'
import { useAlertPopupsContext } from 'context/AlertPopups'
import { TTS_LANGUAGE_MISMATCH_ERROR_IMG } from 'consts/images'
import { useVisualMediaPlacement } from 'hooks/timeline/useVisualMediaPlacement'
import { useResponsive } from 'hooks/useResponsive'
import { translate } from 'utils/translations'
import { T } from 'consts/translationKeys'
import {
  DEFAULT_MEDIA_DURATION,
  PROJECT_MEDIA_TYPES,
  SMALL_DESKTOP_SIZE_BREAKPOINT
} from 'consts'

import { resolveVariant, calcAvatarCost } from './utils'

export const useAvatarGenerate = () => {
  const isFreeUser = useSelector(userSelectors.isFreeUser)
  const hasAvatar = useSelector(aiAvatarSelectors.hasAvatar)
  const activeTimelineItem = useSelector(editorSelectors.activeTimelineItem)
  const activeMedia = useSelector(projectDataSelectors.activeMedia)
  // Regeneration mode = an existing, FINISHED avatar clip is selected on the
  // timeline (the tab was opened from a clip, not for a fresh generation). A
  // clip that is still generating (PENDING) can't be regenerated, so a Generate
  // click on it falls through to a fresh generation instead. `activeMedia` must
  // still exist: a failed fresh generation removes its placeholder but can
  // leave it selected, and a missing clip is a fresh generation, not a regen.
  const isRegenerating =
    activeTimelineItem.type === PROJECT_MEDIA_TYPES.aiAvatar &&
    !!activeMedia &&
    activeMedia.meta?.status !== MediaStatus.PENDING
  const speechText = useSelector(aiAvatarSelectors.speechText)
  const quality = useSelector(aiAvatarSelectors.quality)
  const oneSecondOption = useSelector(
    aiAvatarSelectors.avatarModelOneSecondOption
  )
  const openUpgradePopup = useOpenUpgradePopup()
  const getPlacement = useVisualMediaPlacement()
  // Mobile/tablet shows the AI Avatar tab as a slide-up sub-sidebar over the
  // timeline; the same breakpoint the Sidebar/SubSidebar use to switch layouts.
  const isMobile = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)

  // Estimated credit cost of the typed speech (chars→seconds × quality). It's an
  // estimate — the server does the authoritative cost calc on the actual request.
  const estimatedCost = useMemo(
    () => calcAvatarCost(speechText, quality, oneSecondOption),
    [speechText, quality, oneSecondOption]
  )

  // Free-trial eligibility comes from the gift-checker (free users only). Treat
  // the trial as "used" only once we positively learn it isn't accessible, so we
  // don't flash the premium/upgrade state while the check is still resolving.
  const giftAccess = useSelector(aiAvatarSelectors.giftAccessData)
  const isGiftAccessLoading = useSelector(aiAvatarSelectors.isGiftAccessLoading)
  const hasUsedFreeTrial = !!giftAccess && !giftAccess.isAccessible

  // A free user who has used their gift trial can still generate if they hold a
  // (confirmed positive) credit balance — gift access and credits are
  // independent. `null` = unknown (fetch not resolved / failed); treat it as no
  // usable credits so a gift-exhausted free user defaults to the upgrade CTA
  // (their common state). Credits are fetched at login, so the null window is
  // effectively never seen in this footer.
  const userCredits = useSelector(creditsSelectors.userCredits)
  const hasCredits = userCredits !== null && userCredits > 0

  const variant = resolveVariant(isFreeUser, hasUsedFreeTrial, hasCredits)

  // A credits-variant user who can't cover this generation is routed to the
  // premium (upgrade) button while keeping the refill state, instead of letting
  // the request fail server-side after the click. That's either a zero/negative
  // balance, or a (positive) balance the estimated cost exceeds. The cost is only
  // an estimate — the server does the authoritative check — so this is a UX guard,
  // not a hard gate. `null` is "unknown", NOT zero (see `hasCredits` above), so we
  // don't wrongly route a paying user to upgrade during the login-time fetch. The
  // `variant === 'credits'` guard leaves `free-trial` (gift) users unaffected.
  const insufficientCredits =
    variant === 'credits' &&
    userCredits !== null &&
    (userCredits <= 0 || estimatedCost > userCredits)

  // After a free user kicks off a generation, the gift/credit isn't consumed
  // until the voiceover is produced mid-render. Until the `voiceover_generated`
  // socket event re-checks gift access, hold the footer in a loading + disabled
  // state so they can't fire another generation on the now-stale free-trial CTA.
  const isAwaitingGiftRecheck = useSelector(
    aiAvatarSelectors.isAwaitingGiftRecheck
  )

  // Block Generate for a free user until the gift status is known, so they can't
  // act during the brief unknown window (and we don't act on the wrong variant).
  const isGiftPending =
    isFreeUser && (isGiftAccessLoading || giftAccess === null)
  // Block Generate while a voice-clone upload is in flight on the Clone
  // sub-tab: `activeRefAudio` isn't set until the upload resolves, so firing
  // now would silently generate with the catalog speaker instead of the clone.
  const isVoiceCloneUploading = useSelector(
    aiAvatarSelectors.isActiveVoiceCloneUploading
  )
  // Block Generate while the source image is being enhanced/resized — the
  // reference is mid-reframe, so a talking-head render must wait for it. The
  // thunks set this true on kickoff and the ai-image socket clears it on
  // completion (also covers the pre-response window, unlike `pendingSourceImageId`).
  const isAvatarLoading = useSelector(aiAvatarSelectors.isAvatarLoading)
  // Covers the click→dispatch window while the language pre-check is in
  // flight, so the button spins and double-clicks are blocked (`isGenerating`
  // only starts inside the thunks).
  const [isCheckingLanguage, setIsCheckingLanguage] = useState(false)
  const isDisabled =
    !hasAvatar ||
    !speechText.trim() ||
    isGiftPending ||
    isAwaitingGiftRecheck ||
    isVoiceCloneUploading ||
    isAvatarLoading ||
    isCheckingLanguage
  const isGenerating = useSelector(aiAvatarSelectors.isGenerating)
  const isLoading = isGenerating || isAwaitingGiftRecheck || isCheckingLanguage

  // Tooltip that tells the user which input is still missing. Driven by the two
  // user-supplied inputs only (avatar + speech) — not by `isDisabled`, so it never
  // shows a misleading message while the button is merely gift-pending or loading.
  const noAvatar = !hasAvatar
  const noSpeech = !speechText.trim()
  const generateTooltipText = noAvatar
    ? noSpeech
      ? translate(T.SELECT_AVATAR_AND_ENTER_SPEECH_TOOLTIP)
      : translate(T.SELECT_AVATAR_TOOLTIP)
    : noSpeech
      ? translate(T.ENTER_SPEECH_TEXT_TOOLTIP)
      : ''
  // Hide while the spinner shows (isGenerating || isAwaitingGiftRecheck); empty
  // text means both inputs are present (e.g. brief gift-pending window) → no tooltip.
  const isTooltipVisible = !isLoading && !!generateTooltipText

  // The cost reflects the typed speech (chars→seconds × quality), not whether
  // the avatar/quality is ready to generate. Keep showing it while the button
  // is disabled so the user can see what their speech would cost; it naturally
  // reads 0 only when there's no speech text. Generation is still gated by
  // `isDisabled`.
  const displayCost = estimatedCost
  const isApproximateCost = true

  const { setAlertPopupsData } = useAlertPopupsContext()

  // Voice-clone URL (Clone sub-tab active + clone attached). When set, the
  // generation sends `refAudio` and there is no catalog speaker language to
  // compare against — the language pre-check is skipped entirely.
  const refAudio = useSelector(aiAvatarSelectors.activeRefAudio)
  const speakerId = useSelector(aiAvatarSelectors.voiceSpeakerId)

  // Runs the language pre-check, then dispatches the generation (regenerate
  // reuses the selected clip in place; a fresh generation inserts a
  // placeholder at the playhead). On a mismatch we ask the user to confirm;
  // "Generate anyway" re-runs with the check skipped. The check is advisory:
  // any check-API failure (detector down, old backend without the endpoint)
  // falls through to generation — it must never block the paid action.
  const runGenerate = useCallback(
    async (skipLanguageCheck: boolean) => {
      if (!skipLanguageCheck && !refAudio) {
        setIsCheckingLanguage(true)
        try {
          // Check the same text the generation will actually send — the thunk
          // clamps redux text to the plan limit (spec B5 dual-guard), so an
          // unclamped check could describe a tail that's never synthesized.
          const { data } = await checkTtsLanguageMatch_API({
            id: speakerId,
            text: clampSpeechTextForUser(speechText, isFreeUser)
          })
          // Strict false: the check is advisory, so a non-canonical body
          // (isMatch undefined) must fall through to generation, not popup.
          if (data.isMatch === false) {
            // The popup auto-resets on either button or backdrop close, so
            // "Go back" needs no handler. The confirm goes through
            // `runGenerateRef` (not this closure) so the retry re-resolves the
            // generation intent — target clip, placement against the CURRENT
            // media items, playhead — at confirm time, per INV-PROG-5.
            setAlertPopupsData({
              warningPopup: {
                title: translate(T.AVATAR_LANGUAGE_MISMATCH_TITLE),
                description: translate(T.AVATAR_LANGUAGE_MISMATCH_DESCRIPTION),
                imageSrc: TTS_LANGUAGE_MISMATCH_ERROR_IMG,
                buttons: {
                  cancel: { label: translate(T.GO_BACK_LABEL) },
                  confirm: {
                    label: translate(T.GENERATE_ANYWAY_LABEL),
                    onClick: () => runGenerateRef.current(true)
                  }
                }
              }
            })
            return
          }
        } catch {
          // Fail-open: proceed with generation, no popup.
        } finally {
          setIsCheckingLanguage(false)
        }
      }

      // On mobile/tablet the tab is a slide-up panel over the timeline; once a
      // generation actually fires (fresh, regenerate, or free-trial — all reach
      // here), close it so the user lands back on the timeline to watch the
      // clip render. Matches the AI video generate flow. Closing here — not on
      // click — keeps the panel open behind the language-mismatch popup, so a
      // Cancel returns the user to their inputs instead of the timeline.
      if (isMobile) {
        dispatch(editorThunks.setIsMobileSubSidebarOpen(false))
      }

      if (isRegenerating && activeTimelineItem.id) {
        dispatch(aiAvatarThunks.regenerateAvatar(activeTimelineItem.id))
        return
      }
      dispatch(
        aiAvatarThunks.generateAvatar(getPlacement(DEFAULT_MEDIA_DURATION))
      )
    },
    [
      refAudio,
      speakerId,
      speechText,
      isFreeUser,
      isRegenerating,
      activeTimelineItem.id,
      getPlacement,
      setAlertPopupsData,
      isMobile
    ]
  )

  // Latest-instance ref: the store keeps updating (and this hook keeps
  // re-rendering) while the mismatch popup is open, so the confirm button must
  // call the freshest `runGenerate` — not the one captured when the popup
  // opened with a possibly stale item list / regen target.
  const runGenerateRef = useRef(runGenerate)
  runGenerateRef.current = runGenerate

  const onGenerate = useCallback(() => {
    // Refresh free-trial eligibility on click too (not just on completion), so
    // the footer reflects a consumed trial as soon as possible. Self-guards to
    // free users, never blocks the generation below, and is a read-only
    // re-check — harmless even if the user then cancels the mismatch popup.
    dispatch(aiAvatarThunks.checkGiftAccess())

    runGenerate(false)
  }, [runGenerate])

  const onUpgrade = useCallback(() => {
    openUpgradePopup({ ref: REF_LINKS.V_EDITOR_AVATAR_GENERATE })
  }, [openUpgradePopup])

  return {
    variant,
    insufficientCredits,
    isRegenerating,
    isDisabled,
    isLoading,
    generateTooltipText,
    isTooltipVisible,
    displayCost,
    isApproximateCost,
    onGenerate,
    onUpgrade,
    refillRef: REF_LINKS.V_EDITOR_AI_AVATAR_REFILL
  }
}
